import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';
import WallOfDominance from '../components/sections/WallOfDominance';
import VideoComparisonTable from '../components/sections/VideoComparisonTable';
import GlobalEcosystem from '../components/sections/GlobalEcosystem';
import SimpleProcess from '../components/sections/SimpleProcess';
import FAQ from '../components/sections/FAQ';

const YouTubeService = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        {/* Ambient Glow matching the screenshot vibe */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-64 bg-red-600/15 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              YouTube SEO & <br className="hidden md:block"/>
              <span className="text-red-500">Video Promotion</span> Services
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed max-w-3xl mx-auto"
            >
              Increase your video visibility, rank higher on YouTube and Google, and reach the right audience with structured YouTube SEO and promotion campaigns.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto"
            >
              <Button href="#/contact" variant="primary" className="!from-red-600 !to-rose-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(225,29,72,0.4)] w-full sm:w-auto">
                Get Started Now
              </Button>
              <Button href="#/contact" variant="secondary" className="!px-12 !py-5 text-xl font-bold w-full sm:w-auto">
                View Pricing
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted By & Stats Section */}
      <section className="relative py-24 bg-[#0B0F19] border-b border-white/5 overflow-hidden">
        {/* Background grid matching the screenshot */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>
        
        {/* Subtle purple/pink glow from the left as in screenshot */}
        <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-fuchsia-600/10 blur-[150px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <p className="text-center text-sm font-bold text-white mb-12">Trusted by industry leaders:</p>
          
          {/* Marquee Carousel */}
          <div className="relative w-full overflow-hidden flex items-center mb-28">
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0B0F19] to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0B0F19] to-transparent z-10 pointer-events-none"></div>
            
            <motion.div 
              className="flex gap-16 items-center w-max"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ ease: "linear", duration: 35, repeat: Infinity }}
            >
              {/* Render twice for seamless infinite scrolling */}
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex gap-16 items-center px-8">
                  <div className="flex items-center gap-2 text-2xl font-bold text-slate-400 opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    <SafeIcon icon={FiIcons.FiAperture} /> MULTILOGIN
                  </div>
                  <div className="flex items-center gap-2 text-2xl font-bold tracking-widest text-slate-400 opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    <SafeIcon icon={FiIcons.FiGrid} /> ABACUS.AI
                  </div>
                  <div className="flex items-center gap-2 text-3xl font-black tracking-tighter text-slate-400 opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    GAMMA
                  </div>
                  <div className="flex items-center gap-2 text-3xl font-serif italic text-slate-400 opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    klaviyo
                  </div>
                  <div className="flex items-center gap-2 text-2xl font-medium text-slate-400 opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    <SafeIcon icon={FiIcons.FiBox} /> wondershare
                  </div>
                  <div className="flex items-center gap-1 text-2xl font-bold tracking-tight text-slate-400 opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    <SafeIcon icon={FiIcons.FiTrendingUp} /> HighLevel
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center pb-8">
            <div>
              <h3 className="text-6xl md:text-7xl font-bold text-white mb-4 tracking-tight">35+</h3>
              <p className="text-blue-100 text-lg font-medium">Owned Authority Channels</p>
            </div>
            <div>
              <h3 className="text-6xl md:text-7xl font-bold text-white mb-4 tracking-tight">750+</h3>
              <p className="text-blue-100 text-lg font-medium">Top 10 Rankings Secured</p>
            </div>
            <div>
              <h3 className="text-6xl md:text-7xl font-bold text-white mb-4 tracking-tight">100%</h3>
              <p className="text-blue-100 text-lg font-medium">Indexing Rate on Google</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Results Dominate Section */}
      <section className="relative py-24 bg-[#0B0F19] overflow-hidden border-b border-white/5">
        {/* Subtle red/pink glow on the left matching the screenshot */}
        <div className="absolute top-0 left-0 w-full max-w-2xl h-[500px] bg-rose-900/10 blur-[150px] rounded-full pointer-events-none -translate-x-1/2"></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Video Results Dominate Google Search
            </h2>
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto font-medium">
              Why video carousels outperform traditional text results on Google and mobile.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Card: 3.2x More Space */}
            <div className="glass-card p-10 rounded-2xl border-white/10 bg-[#141822] flex flex-col hover:border-white/20 transition-colors">
              <h3 className="text-2xl font-bold text-white mb-4">3.2x More Space</h3>
              <p className="text-slate-400 leading-relaxed text-[17px] mb-10">
                Video carousel results take up <strong className="text-white">3.2x more screen space</strong> on mobile compared to standard text results.
              </p>
              
              <div className="mt-auto rounded-xl overflow-hidden bg-white shadow-xl">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvanBlZw==/bdc272d328db308caa424b1d4737b8fe.jpg" 
                  alt="YouTube Search Results on Google" 
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Right Card: Higher CTR */}
            <div className="glass-card p-10 rounded-2xl border-white/10 bg-[#141822] flex flex-col hover:border-white/20 transition-colors">
              <div className="mb-auto">
                <div className="relative w-40 h-40 mb-12">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Background track */}
                    <circle cx="50" cy="50" r="42" fill="transparent" stroke="#334155" strokeWidth="8" />
                    {/* Progress arc (41% of circumference) 
                        Circumference = 2 * pi * 42 = ~263.89
                        41% of 263.89 = ~108.2
                    */}
                    <circle 
                      cx="50" cy="50" r="42" 
                      fill="transparent" 
                      stroke="#84cc16" 
                      strokeWidth="8"
                      strokeDasharray="108.2 263.89" 
                      strokeLinecap="round" 
                      className="drop-shadow-[0_0_8px_rgba(132,204,22,0.6)]"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-5xl font-bold text-white tracking-tighter">41%</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Higher CTR</h3>
                <p className="text-slate-400 leading-relaxed text-[17px]">
                  Videos appearing on the first page of Google receive <strong className="text-white">41% higher click-through rate</strong> compared to standard text listings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wall of Dominance Section */}
      <WallOfDominance />

      {/* 2 Ways We Drive Lifetime Traffic Section */}
      <section className="relative py-24 bg-[#0B0F19] overflow-hidden border-b border-white/5">
        {/* Subtle purple glow matching the screenshot on the right side */}
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-purple-900/10 blur-[150px] rounded-full pointer-events-none translate-x-1/3 -translate-y-1/2"></div>
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              2 ways we drive lifetime traffic
            </h2>
            <p className="text-lg text-slate-400 font-medium">
              We create two types of SEO video assets based on where your customers search.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Card 1: YouTube SEO Video Assets */}
            <div className="glass-card p-8 md:p-10 rounded-2xl border-white/10 bg-[#12151e] flex flex-col hover:border-white/20 transition-colors">
              <div className="w-16 h-16 rounded-2xl bg-[#ef4444] flex items-center justify-center mb-8 shadow-lg shadow-red-500/20">
                <SafeIcon icon={FiIcons.FiSearch} className="text-3xl text-white" strokeWidth={3} />
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-4">YouTube SEO Video Assets</h3>
              <p className="text-slate-400 leading-relaxed text-[15px] mb-8">
                Rank in YouTube search and suggested, bringing consistent organic views over time.
              </p>
              
              <ul className="space-y-4 mb-12">
                <li className="flex items-center gap-3">
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-xl flex-shrink-0" />
                  <span className="text-slate-300 font-medium text-[15px]">Search demand mapping</span>
                </li>
                <li className="flex items-center gap-3">
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-xl flex-shrink-0" />
                  <span className="text-slate-300 font-medium text-[15px]">Published on our channels</span>
                </li>
                <li className="flex items-center gap-3">
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-xl flex-shrink-0" />
                  <span className="text-slate-300 font-medium text-[15px]">Optimized for high CTR</span>
                </li>
              </ul>
              
              <div className="mt-auto">
                <a href="#/contact" className="w-full py-4 bg-[#ef4444] hover:bg-[#dc2626] text-white font-bold rounded-2xl transition-all shadow-[0_20px_40px_-10px_rgba(239,68,68,0.5)] active:scale-[0.98] inline-flex items-center justify-center">
                  See YouTube asset example
                </a>
              </div>
            </div>

            {/* Card 2: Google SEO Video Assets */}
            <div className="glass-card p-8 md:p-10 rounded-2xl border-white/10 bg-[#12151e] flex flex-col hover:border-white/20 transition-colors">
              <div className="w-16 h-16 rounded-2xl bg-[#ef4444] flex items-center justify-center mb-8 shadow-lg shadow-red-500/20">
                <SafeIcon icon={FaIcons.FaBullhorn} className="text-3xl text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-4">Google SEO Video Assets</h3>
              <p className="text-slate-400 leading-relaxed text-[15px] mb-8">
                Built to appear in Google search results and generate long-term search traffic.
              </p>
              
              <ul className="space-y-4 mb-12">
                <li className="flex items-center gap-3">
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-xl flex-shrink-0" />
                  <span className="text-slate-300 font-medium text-[15px]">Targeting video carousels</span>
                </li>
                <li className="flex items-center gap-3">
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-xl flex-shrink-0" />
                  <span className="text-slate-300 font-medium text-[15px]">Structured discovery metadata</span>
                </li>
                <li className="flex items-center gap-3">
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-xl flex-shrink-0" />
                  <span className="text-slate-300 font-medium text-[15px]">Perfect for review/tutorial queries</span>
                </li>
              </ul>
              
              <div className="mt-auto">
                <a href="#/contact" className="w-full py-4 bg-[#ef4444] hover:bg-[#dc2626] text-white font-bold rounded-2xl transition-all shadow-[0_20px_40px_-10px_rgba(239,68,68,0.5)] active:scale-[0.98] inline-flex items-center justify-center">
                  See Google asset example
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Video Beats Blogs Comparison Table */}
      <VideoComparisonTable />

      {/* Global Ecosystem Dominance Section */}
      <GlobalEcosystem />

      {/* Simple Process Section */}
      <SimpleProcess />

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-red-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8">Start Growing Your <span className="text-red-500">YouTube Presence</span></h2>
          <p className="text-xl text-slate-400 mb-12">
            YouTube is one of the most powerful platforms for visibility and long-term growth. Contact us today to discuss your goals and scale your video content.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button href="#/contact" variant="primary" className="!px-10 !py-5 text-xl shadow-[0_0_50px_rgba(239,68,68,0.3)]">Start Growing</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouTubeService;
